import React, { useState, useEffect, forwardRef } from 'react';
import { useContext } from 'react';
import { AppStateContext } from '../../../contexts/AppStateContext';
import { AuthContext } from '../../../contexts/AuthContext';
import { TradingContext } from '../../../contexts/TradingContext';
import { Plus, Trash, ThreeDotsVertical, PencilSquare } from 'react-bootstrap-icons';
import { Button, Modal, Form, Spinner, Table, Dropdown } from 'react-bootstrap';
import { useTheme } from '../../../contexts/ThemeContext';
import { StreamsContext } from '../../../contexts/StreamsContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { useWatchlistTickerDetails } from '../../../hooks/useWatchlistTickerDetails';
import WatchlistsPanelSkeleton from '../../shared/skeletons/WatchlistsPanelSkeleton';
import { netChangePctToDisplayPercent, parseQuoteLast } from '../../../utils/quoteNetChangePct';
import { canAccessTradeWorkspace, supportsMarketData, getUnsupportedAssetClassBlock } from '../../../utils/brokerConnection';
import FixedPositionDropdownMenu from '../../shared/FixedPositionDropdownMenu';
import {
  PREMADE_RS_WATCHLISTS,
  getPremadeRsWatchlist,
  isPremadeWatchlistId,
} from '../../../constants/watchlistDefaults';

const isDefaultWatchlist = (w) => String(w?.id) === 'positions';

const partitionWatchlists = (watchlists = []) => {
  const defaultLists = [];
  const userLists = [];
  for (const w of watchlists) {
    if (isDefaultWatchlist(w)) defaultLists.push(w);
    else userLists.push(w);
  }
  return { defaultLists, userLists };
};

/** Popper needs a ref on the visible toggle element (plain `as="div"` breaks first-open placement). */
const WatchlistDropdownToggle = forwardRef(
  ({ children, onClick, style, className, id, ...props }, ref) => (
    <div
      ref={ref}
      id={id}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      className={className}
      style={style}
      {...props}
    >
      {children}
    </div>
  )
);
WatchlistDropdownToggle.displayName = 'WatchlistDropdownToggle';

const WatchlistSortToggle = forwardRef(
  ({ onClick, style, className, title, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title={title}
      onClick={onClick}
      className={className}
      style={style}
      {...props}
    >
      {children}
    </button>
  )
);
WatchlistSortToggle.displayName = 'WatchlistSortToggle';

const watchlistDropdownMenuStyle = (colors, zIndex = 900) => ({
  backgroundColor: colors.bgSecondary,
  border: `1px solid ${colors.borderLight}`,
  borderRadius: 8,
  padding: '4px 0',
  marginTop: 4,
  minWidth: 180,
  maxHeight: 'min(70vh, 480px)',
  overflowY: 'auto',
  fontSize: '0.875rem',
  zIndex,
});

const watchlistSectionHeaderStyle = (colors) => ({
  color: colors.textMuted,
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '8px 16px 4px',
});

const watchlistItemStyle = (colors, isActive) => ({
  color: isActive ? colors.primary : colors.text,
  backgroundColor: isActive ? `${colors.primary}1A` : 'transparent',
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: isActive ? 600 : 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
});

/**
 * Shared watchlist picker menu: Default lists (e.g. Positions), user lists
 * with rename/delete, then the same Popular lists as Relative Strength.
 */
const WatchlistDropdownMenuItems = ({
  watchlists,
  selectedWatchlistId,
  colors,
  onSelect,
  onRename,
  onDelete,
  onCreate,
  stopPropagation = false,
}) => {
  const { defaultLists, userLists } = partitionWatchlists(watchlists);

  const renderItem = (w, { readOnly = false } = {}) => {
    const isActive = String(w.id) === String(selectedWatchlistId);
    const canManage = !readOnly && !isDefaultWatchlist(w);
    return (
      <Dropdown.Item
        key={String(w.id)}
        data-testid={`watchlist-option-${w.id}`}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          onSelect(w.id);
        }}
        style={watchlistItemStyle(colors, isActive)}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = `${colors.primary}0D`;
            e.currentTarget.style.color = colors.primary;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = colors.text;
          }
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {w.name}
        </span>
        {canManage && (
          <span
            data-testid={`watchlist-actions-${w.id}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            <button
              className="btn btn-sm btn-link"
              title="Rename watchlist"
              onClick={(e) => {
                e.stopPropagation();
                onRename(w);
              }}
              style={{ color: colors.primary, padding: 0, textDecoration: 'none', fontWeight: 400 }}
            >
              <PencilSquare size={14} />
            </button>
            <button
              className="btn btn-sm btn-link"
              title="Delete watchlist"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(w);
              }}
              style={{ color: colors.primary, padding: 0, textDecoration: 'none', fontWeight: 400 }}
            >
              <Trash size={14} />
            </button>
          </span>
        )}
      </Dropdown.Item>
    );
  };

  const hasUserOrDefault = defaultLists.length > 0 || userLists.length > 0;

  return (
    <>
      {defaultLists.length > 0 && (
        <>
          <Dropdown.Header
            data-testid="watchlist-section-default"
            style={watchlistSectionHeaderStyle(colors)}
          >
            Default
          </Dropdown.Header>
          {defaultLists.map((w) => renderItem(w))}
          {userLists.length > 0 && <Dropdown.Divider />}
        </>
      )}
      {userLists.length > 0 && (
        <>
          <Dropdown.Header
            data-testid="watchlist-section-my-lists"
            style={watchlistSectionHeaderStyle(colors)}
          >
            My lists
          </Dropdown.Header>
          {userLists.map((w) => renderItem(w))}
        </>
      )}
      {PREMADE_RS_WATCHLISTS.length > 0 && (
        <>
          {hasUserOrDefault && <Dropdown.Divider />}
          <Dropdown.Header
            data-testid="watchlist-section-popular"
            style={watchlistSectionHeaderStyle(colors)}
          >
            Popular
          </Dropdown.Header>
          {PREMADE_RS_WATCHLISTS.map((w) => renderItem(w, { readOnly: true }))}
        </>
      )}
      <Dropdown.Divider />
      <Dropdown.Item
        data-testid="watchlist-create-option"
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          onCreate();
        }}
        style={{ padding: '8px 16px', fontSize: '0.875rem', color: colors.primary }}
      >
        + New watchlist…
      </Dropdown.Item>
    </>
  );
};

const WatchlistsPanel = ({ hideHeader = false, controlsOnly = false, selectOnly = false, onTickerSelect, onTickerClick, highlightedTicker }) => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const canSelectTicker = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const {
    watchlists,
    selectedWatchlistId,
    setSelectedWatchlistId,
    watchlistTickers: tickers,
    watchlistsLoading,
    createWatchlist,
    deleteWatchlist,
    renameWatchlist,
    addWatchlistTicker: addTicker,
    removeWatchlistTicker: removeTicker,
    reorderWatchlistTickers: reorderTickers,
    activeChartId,
    updateChartSettings,
    chartLayout,
  } = useContext(AppStateContext);

  const [dragIndex, setDragIndex] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [modalMode, setModalMode] = useState(null); // 'rename' | 'delete' | 'create'
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showListDropdown, setShowListDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState(null); // 'name' | 'pct' | 'volume'
  const [isEditMode, setIsEditMode] = useState(false);
  const isMobile = useIsMobile();
  const isLoggedIn = !!user?.token;
  const hasMarketData = supportsMarketData(user, selectedAccount);
  const showQuotes = hasMarketData;
  const { quotesBySymbol } = useContext(StreamsContext);
  const { getTickerDetails } = useWatchlistTickerDetails(tickers);

  // Shared formatters for price and percentage values
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const isPositionsList = selectedWatchlistId === 'positions';
  const isPremadeList = isPremadeWatchlistId(selectedWatchlistId);
  const isReadOnlyList = isPositionsList || isPremadeList;
  const selectedPremade = getPremadeRsWatchlist(selectedWatchlistId);
  const selectedWatchlist = selectedPremade
    ? { id: selectedPremade.id, name: selectedPremade.name }
    : watchlists.find(w => String(w.id) === String(selectedWatchlistId));
  const selectedWatchlistName = selectedWatchlist?.name || '';
  const hasRealWatchlists = watchlists.some((w) => w.id !== 'positions');
  const watchlistBodyLoading =
    hasMarketData &&
    (watchlistsLoading || (isLoggedIn && !selectedWatchlistId && !hasRealWatchlists));

  useEffect(() => {
    if (isReadOnlyList) setIsEditMode(false);
  }, [isReadOnlyList]);

  // Sort tickers for display (name, % up/down, volume)
  const displayTickers = React.useMemo(() => {
    if (!tickers || tickers.length === 0 || !sortBy) return tickers || [];
    const arr = [...tickers];
    if (sortBy === 'name') {
      arr.sort((a, b) => (a.ticker || '').localeCompare(b.ticker || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'pct') {
      arr.sort((a, b) => {
        const qa = quotesBySymbol?.[a.ticker];
        const qb = quotesBySymbol?.[b.ticker];
        const pctA = qa ? netChangePctToDisplayPercent(qa) : -Infinity;
        const pctB = qb ? netChangePctToDisplayPercent(qb) : -Infinity;
        return (pctB ?? -Infinity) - (pctA ?? -Infinity); // desc: highest % first
      });
    } else if (sortBy === 'volume') {
      arr.sort((a, b) => {
        const qa = quotesBySymbol?.[a.ticker];
        const qb = quotesBySymbol?.[b.ticker];
        const volA = qa ? parseFloat(qa.TotalVolume || qa.Volume || 0) : 0;
        const volB = qb ? parseFloat(qb.TotalVolume || qb.Volume || 0) : 0;
        return volB - volA; // desc: highest volume first
      });
    }
    return arr;
  }, [tickers, sortBy, quotesBySymbol]);

  const openRenameWatchlist = (w) => {
    setRenameError('');
    setRenameName(w.name);
    setSelectedWatchlistId(w.id);
    setModalMode('rename');
  };

  const openDeleteWatchlist = (w) => {
    setDeleteError('');
    setSelectedWatchlistId(w.id);
    setModalMode('delete');
  };

  const openCreateWatchlist = () => {
    setCreateError('');
    setNewListName('');
    setModalMode('create');
  };

  const watchlistMenuProps = {
    watchlists,
    selectedWatchlistId,
    colors,
    onSelect: setSelectedWatchlistId,
    onRename: openRenameWatchlist,
    onDelete: openDeleteWatchlist,
    onCreate: openCreateWatchlist,
  };

  // Removed window event orchestration; modals are opened via local state only

  if (selectOnly) {
    const selectedLabel = selectedWatchlist ? selectedWatchlist.name : 'Select list';
    return (
      <>
        <div
          style={{ display: 'flex', gap: 4, alignItems: 'center', width: isMobile ? '100%' : undefined }}
          onClickCapture={(e) => e.stopPropagation()}
          onTouchStartCapture={(e) => e.stopPropagation()}
        >
          <div
            onClick={(e) => { e.stopPropagation(); setShowListDropdown(true); }}
            style={{
              backgroundColor: colors.bgPrimary,
              border: 'none',
              color: colors.primary,
              borderRadius: 6,
              padding: '6px 8px',
              height: 28,
              lineHeight: 1,
              minWidth: isMobile ? 0 : 160,
              flex: isMobile ? 1 : undefined,
              width: isMobile ? '100%' : undefined,
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
          >
            {selectedLabel}
          </div>
          <Dropdown
            show={showListDropdown}
            onToggle={(isOpen) => setShowListDropdown(isOpen)}
            align="end"
            onClickCapture={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }}
          >
            <Dropdown.Toggle
              as={Button}
              variant="outline-custom"
              onClick={(e) => { e.stopPropagation(); setShowListDropdown(!showListDropdown); }}
              style={{
                backgroundColor: colors.bgPrimary,
                border: 'none',
                color: colors.primary,
                padding: '8px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                boxShadow: 'none',
                outline: 'none',
                whiteSpace: 'nowrap'
              }}
            />
            <FixedPositionDropdownMenu
              sameWidth={isMobile}
              offset={[0, 6]}
              padding={8}
              onClickCapture={(e) => e.stopPropagation()}
              style={watchlistDropdownMenuStyle(colors, 900)}
            >
              <WatchlistDropdownMenuItems {...watchlistMenuProps} stopPropagation />
            </FixedPositionDropdownMenu>
          </Dropdown>
        </div>

        {/* Unified modal for selectOnly context */}
        <Modal
          show={!!modalMode}
          onHide={() => { setModalMode(null); setCreateError(''); setRenameError(''); setDeleteError(''); }}
          centered
        >
          <Modal.Header 
            closeButton
            closeVariant="white"
            style={{ 
              backgroundColor: colors.bgSecondary, 
              borderBottom: `1px solid ${colors.borderLight}`,
              color: colors.text
            }}
          >
            <Modal.Title style={{ color: colors.text }}>
              {modalMode === 'addTicker'}
              {modalMode === 'rename'}
              {modalMode === 'delete'}
              {modalMode === 'create'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: colors.bgCard, color: colors.text }}>
            {modalMode === 'create' && (
              <Form
                id="unified-create-watchlist-form--selectonly"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newListName.trim() || creating) return;
                  setCreateError('');
                  setCreating(true);
                  try {
                    await createWatchlist(newListName.trim());
                    setNewListName('');
                    setModalMode(null);
                  } catch (err) {
                    const msg = err?.message || 'Failed to create watchlist';
                    setCreateError(msg.includes('exists') ? 'A list with that name already exists.' : msg);
                  } finally {
                    setCreating(false);
                  }
                }}
              >
                <Form.Group>
                  <Form.Label style={{ color: colors.textLight }}>List Name</Form.Label>
                  <Form.Control
                    autoFocus
                    value={newListName}
                    disabled={creating}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="e.g., Tech, Futures, Momentum"
                    className="create-watchlist-input"
                    style={{
                      backgroundColor: colors.bgInput,
                      borderColor: colors.border,
                      color: colors.white
                    }}
                  />
                </Form.Group>
                {createError && (
                  <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{createError}</div>
                )}
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.borderLight}` }}>
            <Button 
              variant="secondary" 
              onClick={() => { setModalMode(null); setCreateError(''); }} 
              style={{ 
                backgroundColor: colors.transparent,
                borderColor: colors.borderLight,
                color: colors.text
              }}
            >
              Cancel
            </Button>
            {modalMode === 'create' && (
              <Button
                variant="primary"
                type="submit"
                form="unified-create-watchlist-form--selectonly"
                disabled={!newListName.trim() || creating}
                style={{ 
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                  color: colors.white
                }}
              >
                {creating ? (<><Spinner size="sm" animation="border" /> Creating...</>) : 'Create'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  if (controlsOnly) {
    const selectedLabel = selectedWatchlist ? selectedWatchlist.name : 'Select list';
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: isMobile ? '100%' : undefined }}>
        <div style={{ flex: isMobile ? 1 : undefined, minWidth: isMobile ? 0 : undefined, width: isMobile ? '100%' : undefined }}>
          <Dropdown
            show={showListDropdown}
            onToggle={(isOpen) => setShowListDropdown(isOpen)}
            align="end"
            className={isMobile ? 'w-100' : undefined}
          >
            <Dropdown.Toggle
              as={WatchlistDropdownToggle}
              id="watchlist-controlsonly-dropdown"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: colors.bgSecondary,
                border: 'none',
                color: colors.primary,
                borderRadius: 6,
                padding: '6px 8px',
                height: 28,
                lineHeight: 1,
                minWidth: isMobile ? 0 : 140,
                maxWidth: isMobile ? 'none' : '40vw',
                width: isMobile ? '100%' : undefined,
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              {selectedLabel}
            </Dropdown.Toggle>
            <FixedPositionDropdownMenu
              sameWidth={isMobile}
              offset={[0, 6]}
              padding={8}
              style={watchlistDropdownMenuStyle(colors, 900)}
            >
              <WatchlistDropdownMenuItems {...watchlistMenuProps} stopPropagation />
            </FixedPositionDropdownMenu>
          </Dropdown>
        </div>
        <button
          className="btn btn-sm btn-link"
          title={!isLoggedIn ? "Please log in to add tickers" : "Add symbol"}
          onClick={() => { 
            window.dispatchEvent(new CustomEvent('ticker:add-to-watchlist', {
              detail: { onAdd: async (ticker) => { await addTicker(ticker); } }
            }));
          }}
          disabled={isReadOnlyList || !isLoggedIn}
          style={{
            color: colors.white,
            opacity: (!isLoggedIn || isReadOnlyList) ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            textDecoration: 'none',
            border: 'none',
            background: 'none',
            flexShrink: 0,
            marginLeft: 'auto',
          }}
        >
          <Plus size={22} />
        </button>
        {/* Header-side controls removed; actions live in the dropdown items */}
        {/* Unified modal for controlsOnly context */}
        <Modal
          show={!!modalMode}
          onHide={() => { setModalMode(null); setCreateError(''); setRenameError(''); setDeleteError(''); }}
          centered
        >
          <Modal.Header 
            closeButton
            closeVariant="white"
            style={{ 
              backgroundColor: colors.bgSecondary, 
              borderBottom: `1px solid ${colors.borderLight}`,
              color: colors.text
            }}
          >
            <Modal.Title style={{ color: colors.text }}>
              {modalMode === 'rename' && 'Rename Watchlist'}
              {modalMode === 'delete' && 'Delete Watchlist'}
              {modalMode === 'create' && 'Create New Watchlist'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: colors.bgCard, color: colors.text }}>
            {modalMode === 'rename' && (
              <Form
                id="unified-rename-watchlist-form--controls"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!renameName.trim() || renaming || !selectedWatchlistId) return;
                  setRenameError('');
                  setRenaming(true);
                  try {
                    await renameWatchlist(selectedWatchlistId, renameName.trim());
                    setModalMode(null);
                  } catch (err) {
                    const msg = err?.message || 'Failed to rename watchlist';
                    setRenameError(msg.includes('exists') ? 'A list with that name already exists.' : msg);
                  } finally {
                    setRenaming(false);
                  }
                }}
              >
                <Form.Group>
                  <Form.Label style={{ color: colors.textLight }}>New Name</Form.Label>
                  <Form.Control
                    autoFocus
                    value={renameName}
                    disabled={renaming}
                    onChange={(e) => setRenameName(e.target.value)}
                    placeholder="e.g., Tech, Futures, Momentum"
                    style={{
                      backgroundColor: colors.bgInput,
                      borderColor: colors.border,
                      color: colors.white
                    }}
                  />
                </Form.Group>
                {renameError && (
                  <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{renameError}</div>
                )}
              </Form>
            )}
            {modalMode === 'delete' && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  Are you sure you want to delete the watchlist{selectedWatchlistName ? ` "${selectedWatchlistName}"` : ''}? This cannot be undone.
                </div>
                {deleteError && (
                  <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{deleteError}</div>
                )}
              </div>
            )}
            {modalMode === 'create' && (
              <Form
                id="unified-create-watchlist-form--controls"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newListName.trim() || creating) return;
                  setCreateError('');
                  setCreating(true);
                  try {
                    await createWatchlist(newListName.trim());
                    setNewListName('');
                    setModalMode(null);
                  } catch (err) {
                    const msg = err?.message || 'Failed to create watchlist';
                    setCreateError(msg.includes('exists') ? 'A list with that name already exists.' : msg);
                  } finally {
                    setCreating(false);
                  }
                }}
              >
                <Form.Group>
                  <Form.Label style={{ color: colors.textLight }}>List Name</Form.Label>
                  <Form.Control
                    autoFocus
                    value={newListName}
                    disabled={creating}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="e.g., Tech, Futures, Momentum"
                    className="create-watchlist-input"
                    style={{
                      backgroundColor: colors.bgInput,
                      borderColor: colors.border,
                      color: colors.white
                    }}
                  />
                </Form.Group>
                {createError && (
                  <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{createError}</div>
                )}
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.borderLight}` }}>
            <Button 
              variant="secondary" 
              onClick={() => { setModalMode(null); setCreateError(''); setRenameError(''); setDeleteError(''); }} 
              style={{ 
                backgroundColor: colors.transparent,
                borderColor: colors.borderLight,
                color: colors.text
              }}
            >
              Cancel
            </Button>
            {modalMode === 'rename' && (
              <Button
                variant="primary"
                type="submit"
                form="unified-rename-watchlist-form--controls"
                disabled={!renameName.trim() || renaming}
                style={{ 
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                  color: colors.white
                }}
              >
                {renaming ? (<><Spinner size="sm" animation="border" /> Saving...</>) : 'Save'}
              </Button>
            )}
            {modalMode === 'delete' && (
              <Button
                variant="danger"
                onClick={async () => {
                  if (!selectedWatchlistId || isReadOnlyList) return;
                  setDeleteError('');
                  setDeleting(true);
                  try {
                    await deleteWatchlist(selectedWatchlistId);
                    setModalMode(null);
                  } catch (err) {
                    setDeleteError(err?.message || 'Failed to delete watchlist');
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                style={{
                  backgroundColor: colors.danger,
                  borderColor: colors.danger,
                  color: colors.white
                }}
              >
                {deleting ? (<><Spinner size="sm" animation="border" /> Deleting...</>) : 'Delete'}
              </Button>
            )}
            {modalMode === 'create' && (
              <Button
                variant="primary"
                type="submit"
                form="unified-create-watchlist-form--controls"
                disabled={!newListName.trim() || creating}
                style={{ 
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                  color: colors.white
                }}
              >
                {creating ? (<><Spinner size="sm" animation="border" /> Creating...</>) : 'Create'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: isMobile ? 0 : 8, backgroundColor: colors.bgPrimary, minWidth: 0, overflow: 'visible', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {!hideHeader && (
      <div
        data-testid="watchlist-header-controls"
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          margin: 0,
          padding: '8px 12px 8px 12px',
          borderBottom: `1px solid ${colors.borderLight}`,
          flexShrink: 0,
          width: '100%',
        }}
      >
        <div
          data-testid="watchlist-dropdown-wrap"
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Dropdown
            show={showListDropdown}
            onToggle={(isOpen) => setShowListDropdown(isOpen)}
            align="end"
            className={isMobile ? 'w-100' : undefined}
          >
            <Dropdown.Toggle
              as={WatchlistDropdownToggle}
              id="watchlist-header-dropdown"
              className="watchlist-dropdown-no-caret"
              data-testid="watchlist-header-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowListDropdown(!showListDropdown);
              }}
              style={{
                backgroundColor: colors.bgSecondary,
                border: 'none',
                color: colors.primary,
                borderRadius: 6,
                padding: '6px 8px',
                height: 28,
                lineHeight: 1,
                minWidth: isMobile ? 0 : 140,
                maxWidth: isMobile ? 'none' : '40vw',
                width: isMobile ? '100%' : undefined,
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              {selectedWatchlistName || 'Select list'}
            </Dropdown.Toggle>
            <FixedPositionDropdownMenu
              data-testid="watchlist-header-menu"
              sameWidth={isMobile}
              offset={[0, 6]}
              padding={8}
              style={watchlistDropdownMenuStyle(colors, 9999)}
            >
              <WatchlistDropdownMenuItems {...watchlistMenuProps} />
            </FixedPositionDropdownMenu>
          </Dropdown>
        </div>
        <div
          data-testid="watchlist-header-actions"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
        <button
          className="btn btn-sm btn-link"
          title={!isLoggedIn ? "Please log in to add tickers" : "Add symbol"}
          onClick={() => {
            window.dispatchEvent(new CustomEvent('ticker:add-to-watchlist', {
              detail: { onAdd: async (ticker) => { await addTicker(ticker); } }
            }));
          }}
          disabled={isReadOnlyList || !isLoggedIn}
          style={{
            width: 28,
            height: 28,
            color: colors.textMuted,
            opacity: (!isLoggedIn || isReadOnlyList) ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            textDecoration: 'none',
            border: 'none',
            background: 'none'
          }}
        >
          <Plus size={24} />
        </button>
        <button
          className="btn btn-sm btn-link"
          title={isEditMode ? "Done editing" : "Edit watchlist"}
          onClick={() => setIsEditMode(!isEditMode)}
          disabled={isReadOnlyList || tickers.length === 0}
          style={{
            minWidth: 28,
            height: 28,
            color: colors.textMuted,
            padding: '0 4px',
            textDecoration: 'none',
            fontSize: '0.875rem',
            border: 'none',
            background: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isEditMode ? 'Done' : <PencilSquare size={20} />}
        </button>
        <Dropdown show={showSortDropdown} onToggle={(isOpen) => setShowSortDropdown(isOpen)} align="end">
          <Dropdown.Toggle
            as={WatchlistSortToggle}
            title="Sort watchlist"
            className="watchlist-sort-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setShowSortDropdown(!showSortDropdown);
            }}
            style={{
              width: 28,
              height: 28,
              borderRadius: 4,
              border: 'none',
              backgroundColor: 'transparent',
              color: colors.textMuted,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              cursor: 'pointer'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 14 14" fill="currentColor" style={{ display: 'block' }}>
              <rect x="2" y="2" width="10" height="1.5" rx="0.3" />
              <rect x="3" y="6" width="8" height="1.5" rx="0.3" />
              <rect x="4" y="10" width="6" height="1.5" rx="0.3" />
            </svg>
          </Dropdown.Toggle>
          <FixedPositionDropdownMenu
            sameWidth={false}
            offset={[0, 6]}
            padding={8}
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 8,
              padding: '4px 0',
              minWidth: 160,
              fontSize: '0.875rem',
              zIndex: 9999,
            }}
          >
            <Dropdown.Item onClick={() => { setSortBy('name'); setShowSortDropdown(false); }} style={{ padding: '8px 16px', color: colors.text }}>Sort by Name</Dropdown.Item>
            {showQuotes && (
              <>
                <Dropdown.Item onClick={() => { setSortBy('pct'); setShowSortDropdown(false); }} style={{ padding: '8px 16px', color: colors.text }}>Sort by % Up/Down</Dropdown.Item>
                <Dropdown.Item onClick={() => { setSortBy('volume'); setShowSortDropdown(false); }} style={{ padding: '8px 16px', color: colors.text }}>Sort by Volume</Dropdown.Item>
              </>
            )}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => { setSortBy(null); setShowSortDropdown(false); }} style={{ padding: '8px 16px', color: colors.textMuted }}>Clear sort</Dropdown.Item>
          </FixedPositionDropdownMenu>
        </Dropdown>
        </div>
      </div>
      )}
      {/* Unified modal for add, rename, delete, create */}
      <Modal
        show={!!modalMode}
        onHide={() => { setModalMode(null); setCreateError(''); setRenameError(''); setDeleteError(''); }}
        centered
      >
        <Modal.Header 
          closeButton
          closeVariant="white"
          style={{ 
            backgroundColor: colors.bgSecondary, 
            borderBottom: `1px solid ${colors.borderLight}`,
            color: colors.text
          }}
        >
          <Modal.Title style={{ color: colors.text }}>
            {modalMode === 'rename' && 'Rename Watchlist'}
            {modalMode === 'delete' && 'Delete Watchlist'}
            {modalMode === 'create' && 'Create New Watchlist'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: colors.bgCard, color: colors.text }}>
          {modalMode === 'rename' && (
            <Form
              id="unified-rename-watchlist-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!renameName.trim() || renaming || !selectedWatchlistId) return;
                setRenameError('');
                setRenaming(true);
                try {
                  await renameWatchlist(selectedWatchlistId, renameName.trim());
                  setModalMode(null);
                } catch (err) {
                  const msg = err?.message || 'Failed to rename watchlist';
                  setRenameError(msg.includes('exists') ? 'A list with that name already exists.' : msg);
                } finally {
                  setRenaming(false);
                }
              }}
            >
              <Form.Group>
                <Form.Label style={{ color: colors.textLight }}>New Name</Form.Label>
                <Form.Control
                  autoFocus
                  value={renameName}
                  disabled={renaming}
                  onChange={(e) => setRenameName(e.target.value)}
                  placeholder="e.g., Tech, Futures, Momentum"
                  style={{
                    backgroundColor: colors.bgInput,
                    borderColor: colors.border,
                    color: colors.text
                  }}
                />
              </Form.Group>
              {renameError && (
                <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{renameError}</div>
              )}
            </Form>
          )}
          {modalMode === 'delete' && (
            <div>
              <div style={{ marginBottom: 8 }}>
                Are you sure you want to delete the watchlist{selectedWatchlistName ? ` "${selectedWatchlistName}"` : ''}? This cannot be undone.
              </div>
              {deleteError && (
                <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{deleteError}</div>
              )}
            </div>
          )}
          {modalMode === 'create' && (
            <Form
              id="unified-create-watchlist-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newListName.trim() || creating) return;
                setCreateError('');
                setCreating(true);
                try {
                  await createWatchlist(newListName.trim());
                  setNewListName('');
                  setModalMode(null);
                } catch (err) {
                  const msg = err?.message || 'Failed to create watchlist';
                  setCreateError(msg.includes('exists') ? 'A list with that name already exists.' : msg);
                } finally {
                  setCreating(false);
                }
              }}
            >
              <Form.Group>
                <Form.Label style={{ color: colors.textLight }}>List Name</Form.Label>
                <Form.Control
                  autoFocus
                  value={newListName}
                  disabled={creating}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., Tech, Futures, Momentum"
                  className="create-watchlist-input"
                  style={{
                    backgroundColor: colors.bgInput,
                    borderColor: colors.border,
                    color: colors.text
                  }}
                />
              </Form.Group>
              {createError && (
                <div style={{ color: colors.danger, marginTop: 8, fontSize: 13 }}>{createError}</div>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.borderLight}` }}>
          <Button 
            variant="secondary" 
            onClick={() => { setModalMode(null); setCreateError(''); setRenameError(''); setDeleteError(''); }} 
            style={{ 
              backgroundColor: colors.transparent,
              borderColor: colors.borderLight,
              color: colors.text
            }}
          >
            Cancel
          </Button>
          {modalMode === 'rename' && (
            <Button
              variant="primary"
              type="submit"
              form="unified-rename-watchlist-form"
              disabled={!renameName.trim() || renaming}
              style={{ 
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                color: colors.white
              }}
            >
              {renaming ? (<><Spinner size="sm" animation="border" /> Saving...</>) : 'Save'}
            </Button>
          )}
          {modalMode === 'delete' && (
            <Button
              variant="danger"
              onClick={async () => {
                if (!selectedWatchlistId || isReadOnlyList) return;
                setDeleteError('');
                setDeleting(true);
                try {
                  await deleteWatchlist(selectedWatchlistId);
                  setModalMode(null);
                } catch (err) {
                  setDeleteError(err?.message || 'Failed to delete watchlist');
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={deleting}
              style={{
                backgroundColor: colors.danger,
                borderColor: colors.danger,
                color: colors.white
              }}
            >
              {deleting ? (<><Spinner size="sm" animation="border" /> Deleting...</>) : 'Delete'}
            </Button>
          )}
          {modalMode === 'create' && (
            <Button
              variant="primary"
              type="submit"
              form="unified-create-watchlist-form"
              disabled={!newListName.trim() || creating}
              style={{ 
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                color: colors.white
              }}
            >
              {creating ? (<><Spinner size="sm" animation="border" /> Creating...</>) : 'Create'}
            </Button>
          )}
          </Modal.Footer>
        </Modal>
        {(isLoggedIn || selectedWatchlistId) && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ border: 'none', borderRadius: 0, overflowX: 'hidden', overflowY: 'auto', flex: 1, minWidth: 0, minHeight: 0 }}>
            {watchlistBodyLoading ? (
              <WatchlistsPanelSkeleton rows={3} />
            ) : (!tickers || tickers.length === 0) ? (
              <div style={{ padding: 12, color: colors.textMuted, fontSize: 14 }}>
                {isPositionsList ? 'No positions' : (isPremadeList ? 'No symbols in this list' : (!isLoggedIn ? 'Please log in to add tickers to your watchlist' : 'Add tickers above'))}
              </div>
            ) : (
              <Table
                  hover
                  size="sm"
                  className="watchlist-table"
                  style={{
                    fontSize: '0.8rem',
                    margin: 0,
                    tableLayout: 'fixed',
                    width: '100%',
                    lineHeight: 1.1,
                    '--bs-table-cell-padding-y': '4px',
                    '--bs-table-cell-padding-x': '12px',
                    backgroundColor: colors.bgPrimary,
                    '--bs-table-bg': colors.bgPrimary,
                    '--bs-table-hover-bg': colors.bgSecondary,
                    '--bs-table-border-color': colors.borderLight,
                    color: colors.text
                  }}
                >
                <tbody>
                  {displayTickers.map((row, idx) => (
                      <tr
                      key={row.ticker}
                        draggable={!isReadOnlyList}
                        onDragStart={(e) => {
                          setDragIndex(idx);
                          try {
                            if (e?.dataTransfer) {
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', String(idx));
                            }
                          } catch (_) {}
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          try {
                            if (e?.dataTransfer) {
                              e.dataTransfer.dropEffect = 'move';
                            }
                          } catch (_) {}
                        }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (isReadOnlyList || dragIndex === null || dragIndex === idx) return;
                        const next = [...displayTickers];
                        const [moved] = next.splice(dragIndex, 1);
                        next.splice(idx, 0, moved);
                        const order = next.map(r => r.ticker);
                        reorderTickers(order).catch(() => {});
                        setDragIndex(null);
                      }}
                      onClick={() => {
                        if (!canSelectTicker) return;
                        // Update the active chart's ticker (works for both mobile and desktop)
                        if (activeChartId) {
                          updateChartSettings(activeChartId, { ticker: row.ticker });
                        } else {
                          console.warn('⚠️ [WatchlistsPanel] No activeChartId available');
                        }
                        // Let callers outside the Trade page's chart layout (e.g. dashboard
                        // widgets) know which ticker was clicked, independent of activeChartId.
                        onTickerClick?.(row.ticker);
                        // Switch to chart tab on mobile if callback is provided
                        if (onTickerSelect && isMobile) {
                          console.log('📱 [WatchlistsPanel] Switching to chart tab on mobile');
                          onTickerSelect();
                        }
                        // Do NOT clear bracket/take profit data - let dependencies update automatically
                      }}
                      style={{
                        cursor: !isReadOnlyList && canSelectTicker ? 'grab' : 'default',
                        backgroundColor: row.ticker === highlightedTicker ? `${colors.primary}14` : 'transparent',
                        boxShadow: row.ticker === highlightedTicker ? `inset 2px 0 0 ${colors.primary}` : 'none',
                      }}
                    >
                      <td style={{ verticalAlign: 'middle', paddingLeft: '14px', paddingTop: '9px', paddingBottom: '9px', paddingRight: isMobile ? '18px' : '14px', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}>
                            {!isReadOnlyList && (<ThreeDotsVertical size={14} style={{ opacity: 0.3, color: colors.textLight, flexShrink: 0 }} />)}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, overflow: 'hidden' }}>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: colors.primary,
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  textDecoration: 'none',
                                  transition: 'text-decoration 0.2s ease',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                              >
                                {row.ticker}
                              </span>
                              {(() => {
                                const tickerDetails = getTickerDetails(row.ticker);
                                const companyName = tickerDetails?.Description || tickerDetails?.Root || '';
                                if (companyName) {
                                  return (
                                    <span
                                      style={{
                                        fontSize: '0.75rem',
                                        color: colors.textMuted,
                                        opacity: 0.8,
                                        lineHeight: 1.2,
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      title={companyName}
                                    >
                                      {companyName}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                          {showQuotes && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {(() => {
                              const tickerDetails = getTickerDetails(row.ticker);
                              const unsupported = getUnsupportedAssetClassBlock(
                                user,
                                selectedAccount,
                                row.ticker,
                                tickerDetails
                              );
                              if (unsupported) {
                                return (
                                  <span
                                    title={unsupported.message}
                                    style={{ color: colors.textMuted, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                  >
                                    Not supported
                                  </span>
                                );
                              }
                              const q = quotesBySymbol[row.ticker]
                                || quotesBySymbol[String(row.ticker || '').toUpperCase()];
                              if (!q) {
                                return <span style={{ color: colors.textMuted }}>—</span>;
                              }
                              const last = parseQuoteLast(q);
                              const net = parseFloat(q.NetChange);
                              const pct = netChangePctToDisplayPercent(q);
                              const hasChange = Number.isFinite(net) && Number.isFinite(pct);

                              if (!Number.isFinite(last)) {
                                return <span style={{ color: colors.textMuted }}>—</span>;
                              }
                              const netDisplay = hasChange ? numberFormatter.format(Math.abs(net)) : null;
                              const pctDisplay = hasChange ? numberFormatter.format(Math.abs(pct)) : null;
                              // A move that rounds to +0.00 / +0.00% is flat, not an up day.
                              const isFlat = hasChange && netDisplay === '0.00' && pctDisplay === '0.00';
                              const positive = hasChange && !isFlat && net >= 0;
                              const color = isFlat
                                ? (colors.chartNeutral || colors.text)
                                : (positive ? colors.chartUp : colors.chartDown);
                              const sign = isFlat || positive ? '+' : '-';
                              return (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0, gap: '4px' }}>
                                    <div style={{ color: colors.text, whiteSpace: 'nowrap' }}>{currencyFormatter.format(last)}</div>
                                    {hasChange && (
                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        <span style={{ color }}>{`${sign}${netDisplay}`}</span>
                                        <span style={{ color }}>{`${sign}${pctDisplay}%`}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                          </div>
                          )}
                          {!isReadOnlyList && isEditMode && (
                            <div style={{ textAlign: 'right' }}>
                            <button
                              className="btn btn-sm btn-link"
                              onClick={(e) => { e.stopPropagation(); removeTicker(row.ticker); }}
                              style={{ 
                                padding: 0, 
                                color: colors.textMuted, 
                                flexShrink: 0, 
                                marginLeft: '2px',
                                width: '18px'
                              }}
                              title="Remove from watchlist"
                            >
                              <Trash />
                            </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistsPanel;

